import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpenIcon, ChevronDownIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { isBlankHtml, RichText } from "../KaTeXComponent/RichText";
import { focusRing, linkButtonClass } from "../../pages/Exams/TestBuilder/ui";
import PassageLockNotice, { passageBooklets } from "./PassageLock";
import usePassage from "./usePassage";

/**
 * The passage of a question ({uuid, name}) in bank and builder pages: a collapsible card, the text
 * loaded on first open. `actions(detail)` adds buttons to the header (the detail is then loaded up front).
 * `showLock`: a printed passage (active BTS booklets print its questions) opens with the lock notice above its text,
 * for a page that does not say so itself. Off by default: the question, edit and create pages have their own notice,
 * and nothing in the test builder or the variants changes the passage.
 * In a flex column (the create page) the card keeps its height (shrink-0): the page scrolls instead of squashing it.
 */
export default function PassagePanel({ passage, defaultOpen = false, actions, showLock = false, className = "" }) {
  const [t] = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  const state = usePassage(passage?.uuid && (open || actions) ? passage.uuid : null);
  if (!passage?.uuid) return null;
  const booklets = showLock ? passageBooklets(state.data) : [];

  return (
    <section className={`shrink-0 overflow-hidden rounded-xl border border-indigo-100 bg-indigo-50/50 ${className}`}>
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls={bodyId}
          className={`inline-flex min-w-0 items-center gap-2 rounded-lg px-1 py-1 text-left text-sm font-medium text-indigo-900 transition duration-200 hover:text-indigo-700 ${focusRing}`}
        >
          <BookOpenIcon className="h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />
          <span className="min-w-0 truncate">{t("passages.panel.title", { name: passage.name })}</span>
          <ChevronDownIcon
            className={`h-4 w-4 shrink-0 text-indigo-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
          <span className="sr-only">{open ? t("passages.panel.hide") : t("passages.panel.show")}</span>
        </button>
        {actions && state.data && <div className="ml-auto flex items-center gap-2">{actions(state.data)}</div>}
      </div>
      {open && (
        <div id={bodyId} className="border-t border-indigo-100 bg-white px-4 py-3">
          {state.loading ? (
            <div className="space-y-2" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <span key={i} className="block h-4 rounded bg-slate-100 motion-safe:animate-pulse" />
              ))}
            </div>
          ) : state.error ? (
            <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-red-700">
              <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {t("passages.panel.load_failed")}
              <button type="button" onClick={state.retry} className={linkButtonClass}>
                {t("passages.panel.retry")}
              </button>
            </div>
          ) : (
            <>
              <PassageLockNotice booklets={booklets} compact className="mb-3" />
              {isBlankHtml(state.data?.body) ? (
                <p className="text-sm text-slate-500">{t("passages.panel.empty")}</p>
              ) : (
                <div className="passage-content max-h-[28rem] overflow-y-auto text-sm leading-relaxed text-slate-800 [&_img]:max-h-80 [&_img]:!w-auto">
                  <RichText html={state.data?.body} dollarMath={false} />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
